/**
 * The public contract of the Studio showcase (spec `2026-10-10-music24-studio-showcase-design.md`
 * §3): a live, read-only demo of the music video editor on music24.com, with the frozen "Forests"
 * cut of LaziiBee. music24-api answers these shapes under `/public/music-video-showcase`; the web app
 * reads them in its showcase mode. Only types, constants and pure helpers: every project may read
 * this file.
 */
import type {
  BankFootageItem,
  BankFootageQuery,
  BankFootageResponse,
  MusicVideoEditorResponse,
  SerializedMusicVideoCut,
} from './musicVideoApi';

/** The routes of the showcase, below the music24-api base (`/api_api` on the web). */
export const MUSIC_VIDEO_SHOWCASE_PATHS = {
  /** `GET`: `MusicVideoShowcaseResponse`. */
  showcase: '/public/music-video-showcase',
  /** `GET ?q&mood&setting&people&cursor&limit`: `MusicVideoShowcaseBankResponse` (the published bank). */
  bank: '/public/music-video-showcase/bank',
  /** `GET ?uuids=a,b`: `MusicVideoShowcaseBankResponse` (the showcase clips and the published bank clips). */
  bankLookup: '/public/music-video-showcase/bank/lookup',
} as const;

/**
 * The one cut of the showcase. It is a fixed value that is never in the database, so no account
 * route accepts it.
 */
export const MUSIC_VIDEO_SHOWCASE_CUT_UUID = '5c0a5e00-f0e5-4e57-8a11-000000000001';

/** The type of the message that the embedded demo posts to its parent with its height. */
export const MUSIC_VIDEO_DEMO_HEIGHT_MESSAGE = 'm24-music-video-demo:height';

/** The message that the embedded demo (`/app/music-video-demo?embed=1`) posts to its parent. */
export interface MusicVideoDemoHeightMessage {
  type: typeof MUSIC_VIDEO_DEMO_HEIGHT_MESSAGE;
  /** The CSS pixels of the page. */
  height: number;
}

/** True for a height message of the demo. Pure. */
export function isMusicVideoDemoHeightMessage(value: unknown): value is MusicVideoDemoHeightMessage {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { type, height } = value as Record<string, unknown>;
  return type === MUSIC_VIDEO_DEMO_HEIGHT_MESSAGE && typeof height === 'number' && Number.isFinite(height);
}

/**
 * `GET /public/music-video-showcase` (anonymous, `Cache-Control: public, max-age=300`).
 *
 * - `editor`: the answer of the account editor route. `plan` is closed (`canRender: false`,
 *   `canMakeAiFootage: false`, `creditsLeft: 0`), `cuts` holds the one showcase cut, `musicVideo`
 *   is null and `song.masterCanPlay` is true.
 * - `cut`: the showcase cut (`MUSIC_VIDEO_SHOWCASE_CUT_UUID`), revision 1. Each bank source of its
 *   doc is a showcase clip uuid (`showcaseClipUuid`).
 * - `audioUrl`: the public AAC copy of the song. The editor plays it in place of `master/stream`.
 * - `clips`: the bank items of the clips that the cut uses, with public poster and preview URLs.
 */
export interface MusicVideoShowcaseResponse {
  editor: MusicVideoEditorResponse;
  cut: SerializedMusicVideoCut;
  audioUrl: string;
  clips: BankFootageItem[];
}

/** The query of the showcase bank search: the query of `GET /footage/bank` with no `uuids`. */
export type MusicVideoShowcaseBankQuery = Omit<BankFootageQuery, 'uuids'>;

/** The answer of the showcase bank search and lookup: the answer of `GET /footage/bank`. */
export type MusicVideoShowcaseBankResponse = BankFootageResponse;

/** The most search requests of one client address in one minute (`429 M24_RATE_LIMITED` above it). */
export const MUSIC_VIDEO_SHOWCASE_SEARCH_PER_MINUTE = 30;

const SHA256_PATTERN = /^[0-9a-f]{64}$/;

/**
 * The fixed uuid of a showcase clip: the first 32 hex digits of its SHA-256 in the uuid form, with
 * the version digit set to 4 and the variant digit to 8..b, so every uuid check takes it. The same
 * file always gives the same uuid, in every environment. Throws for a value that is not a SHA-256.
 * Pure.
 */
export function showcaseClipUuid(sha256: string): string {
  const hex = sha256.toLowerCase();
  if (!SHA256_PATTERN.test(hex)) {
    throw new Error('showcaseClipUuid needs a SHA-256 of 64 hex digits');
  }
  const variant = ((parseInt(hex.charAt(16), 16) % 4) + 8).toString(16);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `${variant}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}
